/**
 * NavigatorX Edit User Modal
 * 
 * Modal for editing user profile information including:
 * - First name, last name, display name
 * - Email address
 * - Phone number
 * - Company
 * - Subscription status
 * - Admin notes
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  X,
  Save,
  Loader2,
  User,
  Mail,
  Phone,
  Building2,
  AlertCircle,
  CheckCircle,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { updateUser } from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';
import type { NavUser, NavUserUpdateInput, NavUserSubscriptionStatus } from '../../../types/navigatorx';

interface EditUserModalProps {
  user: NavUser;
  onClose: () => void;
  onSave: (updatedUser: NavUser) => void;
}

const SUBSCRIPTION_OPTIONS: { value: NavUserSubscriptionStatus; label: string; color: string }[] = [
  { value: 'NONE', label: 'None', color: 'bg-gray-100 text-gray-800' },
  { value: 'EMAIL_VERIFIED', label: 'Email Verified', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'TRIAL_ACTIVE', label: 'Trial Active', color: 'bg-blue-100 text-blue-800' },
  { value: 'TRIAL_EXPIRED', label: 'Trial Expired', color: 'bg-red-100 text-red-800' },
  { value: 'SUBSCRIBED', label: 'Subscribed', color: 'bg-green-100 text-green-800' },
  { value: 'SUBSCRIPTION_EXPIRED', label: 'Subscription Expired', color: 'bg-orange-100 text-orange-800' },
  { value: 'REFERRAL_FREE_MONTH', label: 'Referral Free Month', color: 'bg-pink-100 text-pink-800' },
  { value: 'BLOCKED', label: 'Blocked', color: 'bg-red-100 text-red-800' }
];

export default function EditUserModal({ user, onClose, onSave }: EditUserModalProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    company: user.company || '',
    subscriptionStatus: user.subscriptionStatus as NavUserSubscriptionStatus,
    adminNotes: (user as any).adminNotes || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const changed = 
      formData.firstName !== (user.firstName || '') ||
      formData.lastName !== (user.lastName || '') ||
      formData.email !== (user.email || '') ||
      formData.phone !== (user.phone || '') ||
      formData.company !== (user.company || '') ||
      formData.subscriptionStatus !== user.subscriptionStatus ||
      formData.adminNotes !== ((user as any).adminNotes || '');
    setHasChanges(changed);
  }, [formData, user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.email && !formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone) {
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        newErrors.phone = 'Phone number must be at least 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);

    try {
      const adminId = auth.currentUser?.uid || 'unknown';
      
      // Format phone number
      let formattedPhone = formData.phone;
      if (formattedPhone) {
        const digitsOnly = formattedPhone.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
          formattedPhone = '+1' + digitsOnly;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
          formattedPhone = '+' + digitsOnly;
        }
      }

      const updates: NavUserUpdateInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase() || null,
        phone: formattedPhone || null,
        company: formData.company.trim() || null,
        subscriptionStatus: formData.subscriptionStatus,
        adminNotes: formData.adminNotes.trim() || null
      };

      const updatedUser = await updateUser(user.id, updates, adminId);
      
      toast.success(`Updated ${formData.firstName} ${formData.lastName}`);
      onSave(updatedUser);
      onClose();
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">Edit User</h2>
              <p className="text-amber-100 text-sm">{user.email || user.phone}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="hover:bg-white/20 p-1 rounded"
            disabled={saving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="flex items-center gap-1">
                First Name
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-red-500' : ''}
                disabled={saving}
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="lastName" className="flex items-center gap-1">
                Last Name
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-red-500' : ''}
                disabled={saving}
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              Email Address
            </Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
                disabled={saving}
              />
              {user.emailVerified && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
            {user.emailVerified && (
              <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Email verified
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              Phone Number
            </Label>
            <div className="relative">
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className={errors.phone ? 'border-red-500' : ''}
                disabled={saving}
              />
              {user.phoneVerified && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
            {user.phoneVerified && (
              <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Phone verified
              </p>
            )}
          </div>

          {/* Company */}
          <div>
            <Label htmlFor="company" className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              Company
            </Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Optional"
              disabled={saving}
            />
          </div>

          {/* Subscription Status */}
          <div>
            <Label className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-400" />
              Subscription Status
            </Label>
            <select
              value={formData.subscriptionStatus}
              onChange={(e) => handleInputChange('subscriptionStatus', e.target.value)}
              className="w-full border rounded-md px-3 py-2 bg-white mt-1"
              disabled={saving}
            >
              {SUBSCRIPTION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="mt-2">
              <Badge className={SUBSCRIPTION_OPTIONS.find(o => o.value === formData.subscriptionStatus)?.color || ''}>
                {SUBSCRIPTION_OPTIONS.find(o => o.value === formData.subscriptionStatus)?.label || formData.subscriptionStatus}
              </Badge>
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <Label htmlFor="adminNotes" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-gray-400" />
              Admin Notes (Internal)
            </Label>
            <textarea
              id="adminNotes"
              value={formData.adminNotes}
              onChange={(e) => handleInputChange('adminNotes', e.target.value)}
              placeholder="Notes visible only to admins..."
              rows={3}
              className="w-full border rounded-md px-3 py-2 mt-1 resize-none"
              disabled={saving}
            />
          </div>

          {/* Warning about status change */}
          {formData.subscriptionStatus === 'BLOCKED' && user.subscriptionStatus !== 'BLOCKED' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Warning: Blocking User</p>
                <p className="text-red-700">
                  This will immediately revoke the user's access to the app. They will not be able to log in.
                </p>
              </div>
            </div>
          )}

          {/* User ID for reference */}
          <div className="pt-4 border-t">
            <p className="text-xs text-gray-400">
              User ID: <code className="bg-gray-100 px-1 rounded">{user.id}</code>
            </p>
            {user.referralCode && (
              <p className="text-xs text-gray-400 mt-1">
                Referral Code: <code className="bg-gray-100 px-1 rounded">{user.referralCode}</code>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-500">
            {hasChanges ? (
              <span className="text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                Unsaved changes
              </span>
            ) : (
              <span>No changes</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              variant="amber"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
